/**
 * Main Layout - Enterprise-wide navigation and layout
 */

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessEntity, ENTITY_LABELS } from '../contexts/BusinessEntityContext';
import { useBranch } from '../contexts/BranchContext';
import { LogOut, User, ChevronDown, Settings, Home, Beaker, Key, X, ClipboardCheck } from 'lucide-react';
import './MainLayout.css';

// Create Beta Mode context
const BetaModeContext = createContext({ betaMode: false, toggleBetaMode: () => {} });
export const useBetaMode = () => useContext(BetaModeContext);

const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { entity, setEntity, accessibleEntities } = useBusinessEntity();
  const { branch, setBranch, branches, isSuperAdmin: isSuperAdminFromBranch } = useBranch();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [domains, setDomains] = useState([]);
  const [betaMode, setBetaMode] = useState(() => {
    // Load Beta Mode state from localStorage
    return localStorage.getItem('betaMode') === 'true';
  });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const pathParts = location.pathname.split('/');
  const currentDomain = pathParts[1] || '';

  // Determine if we're on the landing page or admin pages
  const isLandingPage = location.pathname === '/';
  const isAdminPage = location.pathname.startsWith('/admin');

  // Get current domain label for display
  const getCurrentDomainLabel = () => {
    if (isLandingPage) return 'Home';
    if (isAdminPage) return 'Admin';
    const domain = domains.find(d => d.id === currentDomain);
    return domain?.label || '';
  };

  const loadDomains = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/config/domains');
      const data = await response.json();
      // Map domains to navigation format
      const mappedDomains = data.map(d => ({
        id: d.domain_key,
        label: d.domain_name,
        icon: getIconEmoji(d.icon),
        description: d.domain_description,
        disabled: !d.is_active
      }));
      setDomains(mappedDomains);
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  }, []);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const getIconEmoji = (iconName) => {
    const iconMap = {
      'TrendingUp': '📊',
      'DollarSign': '💰',
      'Target': '🎯',
      'CheckCircle': '📋',
      'Settings': '🏭',
      'Users': '👥'
    };
    return iconMap[iconName] || '📁';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openChangePasswordModal = () => {
    console.log('openChangePasswordModal called');
    setPasswordChangeData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowUserMenu(false);
    setShowChangePasswordModal(true);
    console.log('Modal state set to true');
  };

  const handleChangePassword = async () => {
    if (!passwordChangeData.newPassword) {
      alert('Password is required');
      return;
    }

    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (passwordChangeData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    console.log('User object:', user);
    const userId = user.user_id || user.id;
    console.log('Using userId:', userId);

    if (!userId) {
      alert('Error: User ID not found. Please log out and log back in.');
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${userId}/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: passwordChangeData.newPassword
        })
      });

      if (response.ok) {
        alert('Password changed successfully');
        setShowChangePasswordModal(false);
        setPasswordChangeData({ newPassword: '', confirmPassword: '' });
      } else {
        const error = await response.json();
        alert(`Failed to change password: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password');
    }
  };

  // Check if user has any data editing privileges (write, edit, create, delete, upload, or admin permissions)
  const hasDataEditingPrivileges = () => {
    if (!user || !user.permissions) return false;
    return user.permissions.some(p => {
      const permName = p.permission_name || '';
      // Check for both : and . separators (e.g., 'domain:edit' or 'domain.edit')
      return permName.includes('write') ||
             permName.includes('edit') ||
             permName.includes('create') ||
             permName.includes('delete') ||
             permName.includes('upload') ||
             permName.includes('admin');
    });
  };

  // Check if user is SuperUser
  const isSuperUser = () => {
    if (!user || !user.roles) return false;
    return user.roles.some(r =>
      r.role_name === 'SuperUser' ||
      r.role_name === 'Super Admin' ||
      r.role_name === 'Admin'
    );
  };

  // Toggle Beta Mode
  const toggleBetaMode = () => {
    const newBetaMode = !betaMode;
    setBetaMode(newBetaMode);
    localStorage.setItem('betaMode', newBetaMode.toString());
  };

  const { hasPermission } = useAuth();

  return (
    <BetaModeContext.Provider value={{ betaMode, toggleBetaMode }}>
      <div className="platform-container">
        <header className="platform-header">
          <div className="header-content">
            <div className="header-branding">
              <h1>ALT BUSINESS</h1>
              <h2>Enterprise Analytics Platform</h2>
            </div>
            <div className="header-info">
              <span className="current-domain">
                {getCurrentDomainLabel()}
              </span>

              <div className="entity-filter">
                <select value={entity} onChange={(e) => setEntity(e.target.value)}>
                  {accessibleEntities.map(code => (
                    <option key={code} value={code}>
                      {ENTITY_LABELS[code] || code}
                    </option>
                  ))}
                </select>
              </div>

              {isSuperAdminFromBranch && (
                <div className="entity-filter" title="Branch filter (Super Admin only)">
                  <select value={branch} onChange={(e) => setBranch(e.target.value)}>
                    {/* Default ALT entry — only render if the dynamic list
                       hasn't already returned an ALT row, so we don't
                       duplicate it. Branches arrive trimmed from the API. */}
                    {!branches.some(b => (b.branch || '').trim() === 'ALT') && (
                      <option value="ALT">Branch: ALT</option>
                    )}
                    <option value="all">Branch: All</option>
                    {branches.map(b => {
                      const code = (b.branch || '').trim();
                      if (!code) return null;
                      return (
                        <option key={code} value={code}>
                          Branch: {code} ({(b.row_count || 0).toLocaleString()})
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              <div className="header-actions">
                <Link to="/" className="admin-button" title="Home">
                  <Home size={18} />
                </Link>
                {(hasPermission('admin:read') || hasDataEditingPrivileges()) && (
                  <>
                    <Link to="/admin/domain-config" className="admin-button" title="Domain Configuration">
                      <Settings size={18} />
                    </Link>
                    <Link to="/admin-panel" className="admin-button" title="User Administration">
                      <User size={18} />
                    </Link>
                    <Link to="/admin/widget-validator" className="admin-button" title="Widget Validator">
                      <ClipboardCheck size={18} />
                    </Link>
                  </>
                )}

                <div className="user-menu-container">
                  <button
                    className="user-menu-button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                  >
                    <User size={18} />
                    <span>{user?.name || user?.username || 'User'}</span>
                    <ChevronDown size={16} />
                  </button>
                  {showUserMenu && (
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <p className="user-name">{user?.full_name || user?.name || user?.username}</p>
                        <p className="user-email">{user?.email || ''}</p>
                        {user?.roles && user.roles.length > 0 && (
                          <p className="user-role">
                            Role: {user.roles.map(r => r.role_name).join(', ')}
                          </p>
                        )}
                        {user?.last_login && (
                          <p className="user-last-login">
                            Last Login: {new Date(user.last_login).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="user-dropdown-divider"></div>
                      <button className="user-dropdown-item" onClick={openChangePasswordModal}>
                        <Key size={16} />
                        <span>Change Password</span>
                      </button>
                      {isSuperUser() && (
                        <>
                          <div className="user-dropdown-divider"></div>
                          <button
                            className={`user-dropdown-item ${betaMode ? 'beta-active' : ''}`}
                            onClick={toggleBetaMode}
                            title="Beta Mode shows all inactive menu items"
                          >
                            <Beaker size={16} />
                            <span>Beta Mode</span>
                            {betaMode && <span className="badge-beta">ON</span>}
                          </button>
                        </>
                      )}
                      <div className="user-dropdown-divider"></div>
                      <button className="user-dropdown-item logout" onClick={handleLogout}>
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hide domain navigation on landing page and admin pages */}
        {!isLandingPage && !isAdminPage && (
          <nav className="domain-nav">
            <div className="nav-container">
              <div className="nav-tabs">
                {domains.map(domain => (
                  <Link
                    key={domain.id}
                    to={`/${domain.id}`}
                    className={`domain-tab ${currentDomain === domain.id ? 'active' : ''} ${domain.disabled ? 'disabled' : ''}`}
                    title={domain.description}
                    onClick={(e) => domain.disabled && e.preventDefault()}
                  >
                    <span className="domain-icon">{domain.icon}</span>
                    <div className="domain-info">
                      <span className="domain-label">{domain.label}</span>
                      {domain.disabled && <span className="badge-coming-soon">Soon</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        )}

        <main className="domain-content">
          <Outlet />
        </main>

        {/* Change Password Modal */}
        {showChangePasswordModal && (
          <div className="modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
              <div className="modal-header">
                <h3>Change Your Password</h3>
                <button onClick={() => setShowChangePasswordModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    New Password <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="password"
                    value={passwordChangeData.newPassword}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, newPassword: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Confirm Password <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="password"
                    value={passwordChangeData.confirmPassword}
                    onChange={(e) => setPasswordChangeData({...passwordChangeData, confirmPassword: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="Re-enter new password"
                  />
                </div>
                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button
                    onClick={() => setShowChangePasswordModal(false)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '4px',
                      background: '#007bff',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      <footer className="platform-footer">
        <div className="footer-content">
          <p>&copy; 2025 ALT Business. All rights reserved.</p>
          <div className="footer-links">
            <a href="/admin" target="_blank" rel="noopener noreferrer">Admin</a>
            <span>|</span>
            <a href="/api/health" target="_blank" rel="noopener noreferrer">API Health</a>
          </div>
        </div>
      </footer>
      </div>
    </BetaModeContext.Provider>
  );
};

export default MainLayout;
